<template>
  <v-container fluid :class="drawer ? 'drawer-open' : 'drawer-closed'">
    <v-row align="start" justify="start">
      <v-col cols="12">
        <v-data-table
          class="inventory-table-display"
          :class="{'not-default-page-size': !this.isDefaultSize}"
          :headers="headers"
          :items="displayInventory"
          :loading="currentPageLoading || !tableInited"
          :server-items-length="totalCount"
          :options="{
            page: pageIndex,
            itemsPerPage: pageSize,
            sortBy: sort.sortBy,
            sortDesc: sort.sortDesc,
          }"
          @update:options="paginate($event)"
          :footer-props="footerProps"
          multi-sort
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title class="d-flex align-center">
                {{ $localizationService.localize("inventory_page.title") }}
                <TableSettings
                  class="ml-5"
                  :pIsVisible="tableInited && !isDefaultSettings"
                  :pSaveButtonTooltip="$localizationService.localize('inventory_page.settings_tooltip.save')"
                  :pDefaultButtonTooltip="$localizationService.localize('inventory_page.settings_tooltip.apply_default')"
                  @onSaveSettings="saveSettings()"
                  @onApplyDefaultSettings="applyDefaultSettings()"
                />
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-text-field
                v-model="searchString"
                :label="$localizationService.localize('inventory_page.search')"
                class="search-field"
                hide-details
                @input="onInputSearchString()"
              ></v-text-field>
              <v-btn
                color="primary"
                class="ml-2"
                @click="openEditInventoryDialog()"
              >
                {{ $localizationService.localize("inventory_page.new_btn") }}
              </v-btn>
              <EditInventoryDialog ref="editInventoryDialog" />
              <ConfirmationDialog ref="deleteDialog" />
            </v-toolbar>
          </template>

          <template v-for="header in headers" v-slot:[`header.${header.value}`]>
            <span
              :key="header.value"
              :class="{
              'not-default-header-sort': isNotDefaultSortItems.includes(header.value)
              }"
            >
              {{ header.text }}
            </span>
          </template>

          <template v-slot:[`item.itemName`]="{ item }">
            <v-tooltip bottom v-if="item.itemName && item.itemName.length > 50" max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="item-name-cell">
                  {{ item.itemName }}
                </div>
              </template>
              <span class="tooltip-text">{{ item.itemName }}</span>
            </v-tooltip>
            <div v-else class="item-name-cell">{{ item.itemName || '-' }}</div>
          </template>

          <template v-slot:[`item.size`]="{ item }">
            {{ item.size === 'NO_SIZE' ? '—' : formatItemSize(item.size) }}
          </template>
          <template v-slot:[`item.createdAt`]="{ item }">
            {{ momentDateAndTime(item.createdAt) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-icon medium class="mr-2" @click="openEditInventoryDialog(item)"
              >mdi-pencil</v-icon
            >
            <v-tooltip bottom v-if="item.assignedQty > 0">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  medium
                  color="grey"
                  v-bind="attrs"
                  v-on="on"
                  class="disabled-icon"
                  >mdi-delete</v-icon
                >
              </template>
              <span>{{ $localizationService.localize("inventory_page.dialog.delete.disabled_tooltip") }}</span>
            </v-tooltip>
            <v-icon
              v-else
              medium
              color="red"
              @click="openDeleteInventoryDialog(item)"
              >mdi-delete</v-icon
            >
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Vue from "vue";
import { mapGetters, mapState } from "vuex";
import lodash from "lodash";
import { momentDateAndTime, formatItemSize } from "@/utils/utils";
import EditInventoryDialog from "./EditInventoryDialog.vue";
import ConfirmationDialog from "@/components/ConfirmationDialog.vue";
import TableSettings from "@/components/TableSettings.vue";
import tableSortParserMixin from "@/utils/table-sort-parser";

const locService = Vue.prototype.$localizationService;

export default {
  name: "Inventory",

  mixins: [tableSortParserMixin],

  components: {
    EditInventoryDialog,
    ConfirmationDialog,
    TableSettings,
  },

  data: () => ({
    localStorageTable: "inventory.table.size",
    tableInited: false,

    footerProps: {
      "items-per-page-options": [25, 50, 100],
    },

    searchString: "",

    headers: [
      {
        text: locService.localize("inventory_page.header.item_name"),
        value: "itemName",
        sortable: true,
      },
      {
        text: locService.localize("inventory_page.header.size"),
        value: "size",
        sortable: true,
      },
      {
        text: locService.localize("inventory_page.header.total_qty"),
        value: "totalQty",
        sortable: true,
      },
      {
        text: locService.localize("inventory_page.header.assigned_qty"),
        value: "assignedQty",
        sortable: true,
      },
      {
        text: locService.localize("inventory_page.header.available_qty"),
        value: "availableQty",
        sortable: true,
      },
      {
        text: locService.localize("inventory_page.header.created_at"),
        value: "createdAt",
        sortable: true,
      },
      {
        text: locService.localize("inventory_page.header.actions"),
        value: "actions",
        sortable: false,
        align: "center",
      },
    ],
  }),

  computed: {
    ...mapState("inventoryModule", [
      "currentPageLoading",
      "currentPage",
      "displayInventory",
      "totalCount",
    ]),
    ...mapState("coreModule", ["drawer"]),
    ...mapState("inventoryTableSettingsModule", [
      "pageIndex",
      "pageSize",
      "sort",
      "defaultSettings",
    ]),
    ...mapGetters("inventoryTableSettingsModule", [
      "isDefaultSettings",
      "isDefaultSort",
      "isDefaultSize",
    ]),

    isNotDefaultSortItems() {
      return !this.isDefaultSort
        ? this.getNotDefaultTableSortItems("sort")
        : [];
    },
  },

  methods: {
    momentDateAndTime,

    formatItemSize,
    paginate(data) {
      this.$store.commit("inventoryTableSettingsModule/changeSettings", {
        index: data.page,
        size: data.itemsPerPage,
        sort: {
          sortBy: [...data.sortBy],
          sortDesc: [...data.sortDesc],
        },
      });

      this.updateRoutePath();

      this.loadPage({
        index: data.page - 1,
        size: data.itemsPerPage,
        sort: data.sortBy.map((it, i) => `${it}:${data.sortDesc[i] ? "desc" : "asc"}`),
        searchString: this.searchString,
      });
    },

    loadPage: lodash.debounce(async function (currentPage) {
      await this.$store.dispatch("inventoryModule/loadPage", currentPage);
      this.tableInited = true;
    }, 500),

    saveSettings() {
      this.$store.commit("inventoryTableSettingsModule/saveSettings");

      Vue.toasted.success(
        locService.localize("inventory_page.settings_msg.save"),
        { duration: 3000 },
      );
    },

    applyDefaultSettings() {
      this.$store.commit("inventoryTableSettingsModule/applyDefaultSettings");
    },

    openEditInventoryDialog(inventory) {
      this.$refs.editInventoryDialog.openDialog(inventory);
    },

    openDeleteInventoryDialog(inventory) {
      this.$refs.deleteDialog.openDialog({
        header: locService.localize("inventory_page.dialog.delete.title"),
        text: locService.localize("inventory_page.dialog.delete.text", [
          inventory.itemName,
        ]),
        submitText: locService.localize("btn.delete"),
        submitColor: "red",
        onSubmit: async () => {
          try {
            const result = await this.$store.dispatch(
              "inventoryModule/deleteInventory",
              inventory,
            );
            Vue.toasted.success(result, {
              duration: 5000,
            });
          } catch (e) {
            Vue.toasted.error(e, {
              duration: 5000,
            });
          }
        },
      });
    },

    reloadPage() {
      this.$store.dispatch("inventoryModule/reloadPage");
    },

    onInputSearchString: lodash.debounce(function () {
      const newPage = {
        index: this.pageIndex - 1,
        size: this.pageSize,
        sort: this.sort.sortBy.map((it, i) => `${it}:${this.sort.sortDesc[i] ? "desc" : "asc"}`),
        searchString: this.searchString,
      };

      this.$store.dispatch("inventoryModule/loadPage", newPage);
      this.updateRoutePath();
    }, 600),

    updateRoutePath() {
      const params = new URLSearchParams();

      params.append("pageSize", this.pageSize);

      params.append("pageIndex", this.pageIndex);

      params.append("sort", JSON.stringify(this.sort));

      if (this.searchString) {
        params.append("searchString", this.searchString);
      }

      if (
        this.$route.query.pageSize != (params.get("pageSize") || undefined)
          || this.$route.query.pageIndex != (params.get("pageIndex") || undefined)
          || this.$route.query.searchString != (params.get("searchString") || undefined)
          || this.$route.query.sort != (params.get("sort") || undefined)
      ) {
        this.$router.push(`/inventory/?${params.toString()}`);
      }
    },
  },

  created() {
    this.$store.dispatch("inventoryTableSettingsModule/loadDefaultSettingsFromStorage");

    const query = this.$route.query || {};

    if (!Object.keys(query).length) {
      this.$store.commit("inventoryTableSettingsModule/applyDefaultSettings");
    } else {
      this.$store.commit("inventoryTableSettingsModule/applyQuerySettings", query);

      if (query.searchString) {
        this.searchString = query.searchString;
      }
    }
  },
};
</script>

<style scoped lang="scss">
.inventory-table-display {
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.search-field {
  max-width: 250px;
}

.item-name-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tooltip-text {
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  line-height: 1.4;
}

.disabled-icon {
  cursor: not-allowed;
  opacity: 0.5;
}

// Desktop: ограничение ширины для Item Name
@media (min-width: 600px) {
  .item-name-cell {
    max-width: 400px;
  }
}

// Mobile: Item Name с меньшим ограничением
@media (max-width: 599px) {
  .item-name-cell {
    max-width: 200px;
  }
}
</style>
